/**
 * The single-camera Recordings page's segment list: one row per recorded chunk — its time span,
 * its length and a download button, nothing more. Clicking a row plays from that chunk.
 *
 * Folds to a slim rail on wide screens, like the camera switcher on the other side; below `lg`
 * the page stacks, so it folds to a header bar under the player instead.
 *
 * Purely presentational: the host owns playback and the download flow (local asks H.264 vs
 * original in a dialog, cloud downloads straight away).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ClockIcon, FilmIcon,
} from '@heroicons/react/24/outline';
import { Tip } from '../common/Tooltip';

export interface SegmentRow { id: string | number; start_time: string; end_time: string; duration?: number | null; [k: string]: unknown }

export interface RecordingSegmentsPanelProps {
  segments: SegmentRow[];
  /** The segment under the playhead — highlighted and kept scrolled into view. */
  activeId?: string | number | null;
  playing?: boolean;
  loading?: boolean;
  /** Under the title, e.g. the applied range and date. */
  subtitle?: string;
  onPlay: (seg: SegmentRow) => void;
  /** Omitted when the viewer may not download. */
  onDownload?: (seg: SegmentRow) => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  className?: string;
}

const fmtClock = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

function fmtLength(seg: SegmentRow) {
  let s = seg.duration && seg.duration > 0 ? seg.duration
    : (new Date(seg.end_time).getTime() - new Date(seg.start_time).getTime()) / 1000;
  if (!Number.isFinite(s) || s <= 0) return '—';
  s = Math.round(s);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** A boolean remembered in this browser only — for per-viewer layout choices like a folded panel. */
export function useStoredToggle(key: string, initial = false): [boolean, () => void] {
  const [value, setValue] = useState(() => {
    try { const v = window.localStorage.getItem(key); return v === null ? initial : v === '1'; } catch { return initial; }
  });
  const toggle = useCallback(() => {
    setValue((v) => {
      try { window.localStorage.setItem(key, v ? '0' : '1'); } catch { /* private mode */ }
      return !v;
    });
  }, [key]);
  return [value, toggle];
}

export default function RecordingSegmentsPanel({
  segments, activeId, playing = false, loading = false, subtitle, onPlay, onDownload,
  collapsed = false, onToggleCollapsed, className = '',
}: RecordingSegmentsPanelProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Follow the playhead down the list. Not while folded — there is no list to scroll.
  useEffect(() => {
    if (collapsed || activeId == null || !listRef.current) return;
    const row = listRef.current.querySelector<HTMLElement>(`[data-seg-id="${CSS.escape(String(activeId))}"]`);
    row?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeId, collapsed]);

  const count = segments.length;

  return (
    <div
      data-tour="recordings-segments"
      className={`relative flex min-h-0 flex-none flex-col transition-[width] duration-300 ease-in-out ${
        collapsed ? 'w-full lg:w-11' : 'max-h-[40vh] w-full lg:max-h-none lg:w-72 xl:w-80'
      } ${className}`}
    >
      {onToggleCollapsed && (
        // On the panel's inner edge, mirroring the camera switcher's — wide screens only; when
        // stacked, the header bar is the toggle.
        <Tip side="left" label={collapsed ? 'Show the segment list' : 'Hide the segment list'} className="absolute -left-3.5 top-3 z-20 hidden lg:block">
          <button
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Show segment list' : 'Hide segment list'}
            aria-expanded={!collapsed}
            className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-border bg-card text-foreground shadow-md transition-colors hover:border-brand hover:text-brand"
          >
            {collapsed ? <ChevronLeftIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
          </button>
        </Tip>
      )}

      {collapsed ? (
        <>
          {/* Wide: the rail. A click anywhere on it opens the list again. */}
          <Tip side="left" label={`Show the segment list (${count})`} className="hidden h-full w-full lg:block">
            <button
              onClick={onToggleCollapsed}
              className="flex h-full w-full flex-col items-center gap-3 overflow-hidden rounded-lg border border-border bg-card pt-12 text-muted-foreground transition-colors hover:text-foreground"
            >
              <FilmIcon className="h-5 w-5 text-brand" />
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-foreground">{count}</span>
              <span className="text-[11px] font-medium tracking-wide [writing-mode:vertical-rl]">Segments</span>
            </button>
          </Tip>
          {/* Stacked: a one-line bar under the player. */}
          <button
            onClick={onToggleCollapsed}
            className="flex w-full items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-left lg:hidden"
          >
            <FilmIcon className="h-4 w-4 flex-none text-brand" />
            <span className="flex-1 text-[13px] font-semibold text-foreground">Segments</span>
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-foreground">{count}</span>
            <ChevronDownIcon className="h-4 w-4 flex-none text-muted-foreground" />
          </button>
        </>
      ) : (
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-card">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2.5 lg:pl-5">
            <div className="min-w-0 flex-1">
              <h3 className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
                Segments
                {!loading && <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{count}</span>}
              </h3>
              {subtitle && <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{subtitle}</p>}
            </div>
            {onToggleCollapsed && (
              <button
                onClick={onToggleCollapsed}
                aria-label="Hide segment list"
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:hidden"
              >
                <ChevronUpIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-1.5" style={{ scrollbarWidth: 'thin' }}>
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-[12px] text-muted-foreground">
                <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted border-t-brand" /> Loading segments…
              </div>
            ) : count === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                <ClockIcon className="mx-auto mb-2 h-7 w-7 opacity-40" />
                <p className="text-[12px]">No segments in this range</p>
              </div>
            ) : (
              <ul className="space-y-0.5">
                {segments.map((seg) => {
                  const active = activeId != null && String(activeId) === String(seg.id);
                  return (
                    <li key={String(seg.id)} data-seg-id={String(seg.id)}>
                      <div
                        className={`group flex items-center gap-2 rounded-md pl-2.5 pr-1 transition-colors ${
                          active ? 'bg-brand-light text-brand' : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        <button
                          onClick={() => onPlay(seg)}
                          aria-current={active ? 'true' : undefined}
                          className="flex min-w-0 flex-1 items-center gap-2 py-2 text-left"
                        >
                          <span className={`h-1.5 w-1.5 flex-none rounded-full ${active ? (playing ? 'animate-pulse bg-red-500' : 'bg-brand') : 'bg-transparent'}`} />
                          <span className={`min-w-0 flex-1 truncate text-[12px] tabular-nums ${active ? 'font-semibold' : 'font-medium'}`}>
                            {fmtClock(seg.start_time)} – {fmtClock(seg.end_time)}
                          </span>
                          <span className={`flex-none text-[11px] tabular-nums ${active ? '' : 'text-muted-foreground'}`}>{fmtLength(seg)}</span>
                        </button>
                        {onDownload && (
                          <Tip side="left" label="Download this segment">
                            <button
                              onClick={() => onDownload(seg)}
                              aria-label="Download this segment"
                              className="flex-none rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
                            >
                              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                            </button>
                          </Tip>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
